/**
 * S9 — Ontology browser (`/ai-studio/ontology`), WF-F6.
 *
 * Read-only master/detail: concept list + detail (relationships + attributes).
 * Extending the ontology is deferred ([L]).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ConceptDetail, ConceptList } from '@/components/ai-studio/ontology';
import {
  listOntologyAttributes,
  listOntologyConcepts,
  listOntologyRelationships,
} from '@/services/aiStudioAgentService';
import {
  ONTOLOGY_CONCEPT_KINDS,
  type AiOntologyAttribute,
  type AiOntologyConcept,
  type AiOntologyRelationship,
  type OntologyConceptKind,
} from '@/types/aiStudioAgent';

const AIStudioOntologyBrowserPage: React.FC = () => {
  const { t } = useLanguage();
  const [concepts, setConcepts] = useState<AiOntologyConcept[]>([]);
  const [relationships, setRelationships] = useState<AiOntologyRelationship[]>(
    []
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [attributes, setAttributes] = useState<AiOntologyAttribute[]>([]);
  const [attributesLoading, setAttributesLoading] = useState(false);
  const [attributesErrorKey, setAttributesErrorKey] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<OntologyConceptKind | ''>('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([listOntologyConcepts(), listOntologyRelationships()])
      .then(([conceptRows, relationshipRows]) => {
        if (cancelled) return;
        setConcepts(conceptRows);
        setRelationships(relationshipRows);
        // Auto-select the first platform concept for a usable empty-state escape.
        if (conceptRows.length > 0) {
          setSelectedId(conceptRows[0]?.id ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setErrorKey('aiStudio.ontology.loadFailed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setAttributes([]);
      setAttributesErrorKey(null);
      return;
    }
    let cancelled = false;
    setAttributesLoading(true);
    setAttributesErrorKey(null);
    listOntologyAttributes(selectedId)
      .then((rows) => {
        if (!cancelled) setAttributes(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setAttributes([]);
          setAttributesErrorKey('aiStudio.ontology.attributesLoadFailed');
        }
      })
      .finally(() => {
        if (!cancelled) setAttributesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const conceptCodeById = useMemo(() => {
    const map = new Map<string, string>();
    concepts.forEach((concept) => map.set(concept.id, concept.code));
    return map;
  }, [concepts]);

  const visibleConcepts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return concepts.filter((concept) => {
      const matchesKind = !kindFilter || concept.conceptKind === kindFilter;
      const matchesTerm =
        !term ||
        concept.code.toLowerCase().includes(term) ||
        concept.name.toLowerCase().includes(term);
      return matchesKind && matchesTerm;
    });
  }, [concepts, kindFilter, search]);

  // Keep detail selection inside the filtered list so list highlight and
  // detail pane cannot disagree after search/kind filter changes.
  useEffect(() => {
    if (visibleConcepts.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!selectedId || !visibleConcepts.some((concept) => concept.id === selectedId)) {
      setSelectedId(visibleConcepts[0]?.id ?? null);
    }
  }, [visibleConcepts, selectedId]);

  const selectedConcept =
    concepts.find((concept) => concept.id === selectedId) ?? null;

  const relatedRelationships = useMemo(() => {
    if (!selectedId) return [];
    return relationships.filter(
      (rel) =>
        rel.fromConceptId === selectedId || rel.toConceptId === selectedId
    );
  }, [relationships, selectedId]);

  return (
    <main className="flex w-full flex-col gap-4 px-4 py-4 md:px-6 lg:px-8">
      <header className="flex flex-col gap-1">
        <h1 className="text-lg font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.ontology.title')}
        </h1>
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.ontology.subtitle')}
        </p>
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.ontology.readOnlyNotice')}
        </p>
      </header>

      {errorKey && (
        <div
          role="alert"
          className="rounded border border-[var(--color-error)] px-4 py-2 text-base text-[var(--color-error)]"
        >
          {t(errorKey)}
        </div>
      )}

      {loading ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.ontology.loading')}
        </p>
      ) : (
        <div className="flex min-h-[28rem] flex-col gap-4 lg:flex-row">
          <aside className="flex w-full flex-col overflow-hidden rounded border border-[var(--color-border)] lg:w-72 lg:shrink-0">
            <div className="border-b border-[var(--color-border)] p-3">
              <h2 className="mb-2 text-base font-semibold text-[var(--color-surface-text)]">
                {t('aiStudio.ontology.concepts')}
              </h2>
              <label className="mb-1 block text-base font-medium text-[var(--color-surface-text)]" htmlFor="ontology-search">
                {t('aiStudio.ontology.search')}
              </label>
              <input
                id="ontology-search"
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="mb-2 w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
              />
              <label className="mb-1 block text-base font-medium text-[var(--color-surface-text)]" htmlFor="ontology-kind">
                {t('aiStudio.ontology.filterKind')}
              </label>
              <select
                id="ontology-kind"
                value={kindFilter}
                onChange={(event) =>
                  setKindFilter(event.target.value as OntologyConceptKind | '')
                }
                className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
              >
                <option value="">{t('aiStudio.ontology.filterAll')}</option>
                {ONTOLOGY_CONCEPT_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {kind}
                  </option>
                ))}
              </select>
            </div>
            <div className="max-h-[28rem] overflow-y-auto">
              <ConceptList
                concepts={visibleConcepts}
                selectedId={selectedId}
                onSelect={setSelectedId}
              />
            </div>
          </aside>

          <section className="min-w-0 flex-1 rounded border border-[var(--color-border)] p-4">
            <ConceptDetail
              concept={selectedConcept}
              relationships={relatedRelationships}
              conceptCodeById={conceptCodeById}
              attributes={attributes}
              attributesLoading={attributesLoading}
              attributesErrorKey={attributesErrorKey}
            />
          </section>
        </div>
      )}
    </main>
  );
};

export default AIStudioOntologyBrowserPage;
